// Work calendar editor inside the Settings modal. Admin-only;
// non-admins see the schedule + holidays read-only-ish (the form
// renders but the toggles are disabled — gated by the
// requireAdmin route on the server, so a forced edit would 403).
//
// Two panels:
//   1. Weekly schedule — M T W T F S S pills + hours-per-day input.
//      Changes save on click / blur.
//   2. Holiday catalogue — list with date + label + recurring
//      checkbox + delete button, plus a "+ Add holiday" form.
//
// Both consumers of the calendar — auto-schedule cascade + Resource
// Management bucketing — pick up the change on the next request, so
// edits here propagate without a full app refresh.
import { useEffect, useState } from "react";
import type {
  WorkHolidayDto,
  WorkScheduleDto,
} from "../../../../../../shared/types/production-schedule";
import {
  createWorkHoliday,
  deleteWorkHoliday,
  updateWorkHoliday,
  updateWorkSchedule,
  useWorkCalendar,
} from "../hooks/useProductionApi";

type Day = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";
const DAYS: { key: Day; label: string }[] = [
  { key: "mon", label: "M" },
  { key: "tue", label: "T" },
  { key: "wed", label: "W" },
  { key: "thu", label: "T" },
  { key: "fri", label: "F" },
  { key: "sat", label: "S" },
  { key: "sun", label: "S" },
];

export function WorkScheduleSection() {
  const { data, refetch, error: fetchError } = useWorkCalendar();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!data) {
    return (
      <div style={{ marginTop: 18, padding: 12, color: "var(--bsp-fg-muted)", fontSize: 12 }}>
        Loading work calendar…
        {fetchError ? (
          <div style={{ color: "var(--bsp-red-fg)", marginTop: 4 }}>{fetchError}</div>
        ) : null}
      </div>
    );
  }

  const onScheduleChange = async (patch: Partial<WorkScheduleDto>) => {
    setBusy(true);
    setError(null);
    try {
      await updateWorkSchedule(patch);
      await refetch();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 18 }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 8,
        paddingBottom: 8,
        borderBottom: "1px solid var(--bsp-border)",
      }}>
        <span style={{
          fontFamily: "var(--bsp-font-display)",
          fontSize: 11,
          fontWeight: 700,
          color: "var(--bsp-blue)",
          textTransform: "uppercase",
          letterSpacing: "0.14em",
        }}>Working hours</span>
      </div>
      <p style={{ fontSize: 11, color: "var(--bsp-fg-muted)", marginBottom: 10, lineHeight: 1.45 }}>
        Which weekdays the workshop runs + a list of holidays. The auto-schedule
        cascade and the Resource Management heatmap both honour these.
      </p>
      {(error ?? fetchError) ? (
        <div style={{ color: "var(--bsp-red-fg)", fontSize: 12, marginBottom: 8 }}>
          {error ?? fetchError}
        </div>
      ) : null}

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--bsp-fg-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Working days
        </span>
        {DAYS.map((d) => (
          <DayToggle
            key={d.key}
            label={d.label}
            active={data.schedule[d.key]}
            disabled={busy}
            onToggle={() => onScheduleChange({ [d.key]: !data.schedule[d.key] } as Partial<WorkScheduleDto>)}
          />
        ))}
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, marginLeft: 12 }}>
          Hours/day
          <HoursPerDayInput
            initial={data.schedule.hoursPerDay}
            disabled={busy}
            onCommit={(v) => onScheduleChange({ hoursPerDay: v })}
          />
        </label>
      </div>

      <HolidaysEditor
        holidays={data.holidays}
        disabled={busy}
        onAfterMutate={refetch}
        onError={setError}
      />
    </div>
  );
}

function DayToggle({
  label,
  active,
  disabled,
  onToggle,
}: {
  label: string;
  active: boolean;
  disabled: boolean;
  onToggle(): void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      style={{
        width: 32,
        height: 32,
        borderRadius: "50%",
        border: `1px solid ${active ? "var(--bsp-blue)" : "var(--bsp-border)"}`,
        background: active ? "var(--bsp-blue)" : "var(--bsp-surface)",
        color: active ? "white" : "var(--bsp-fg-muted)",
        fontWeight: 700,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >{label}</button>
  );
}

function HoursPerDayInput({
  initial,
  disabled,
  onCommit,
}: {
  initial: number;
  disabled: boolean;
  onCommit: (v: number) => void;
}) {
  const [draft, setDraft] = useState(String(initial));
  useEffect(() => { setDraft(String(initial)); }, [initial]);
  return (
    <input
      type="number"
      step={0.5}
      min={0.5}
      max={24}
      value={draft}
      disabled={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const n = Number(draft);
        if (Number.isFinite(n) && n > 0 && n !== initial) onCommit(n);
      }}
      style={{ width: 70, padding: "4px 6px", textAlign: "right", fontFamily: "var(--bsp-font-mono)" }}
    />
  );
}

function HolidaysEditor({
  holidays,
  disabled,
  onAfterMutate,
  onError,
}: {
  holidays: WorkHolidayDto[];
  disabled: boolean;
  onAfterMutate: () => Promise<void>;
  onError: (m: string | null) => void;
}) {
  return (
    <>
      <div style={{ fontSize: 11, fontWeight: 700, color: "var(--bsp-fg-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
        Holidays
      </div>
      {holidays.length === 0 ? (
        <div style={{ fontSize: 11, color: "var(--bsp-fg-muted)", marginBottom: 8 }}>
          No holidays recorded.
        </div>
      ) : (
        <table className="stages-table" style={{ marginBottom: 8 }}>
          <thead>
            <tr>
              <th style={{ width: 140 }}>Date</th>
              <th>Label</th>
              <th style={{ width: 110 }}>Repeats yearly</th>
              <th style={{ width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {holidays.map((h) => (
              <HolidayRow
                key={h.id}
                row={h}
                disabled={disabled}
                onPatch={async (patch) => {
                  onError(null);
                  try {
                    await updateWorkHoliday(h.id, patch);
                    await onAfterMutate();
                  } catch (e) {
                    onError(e instanceof Error ? e.message : String(e));
                  }
                }}
                onRemove={async () => {
                  if (!window.confirm(`Remove holiday "${h.label}" (${h.date})?`)) return;
                  onError(null);
                  try {
                    await deleteWorkHoliday(h.id);
                    await onAfterMutate();
                  } catch (e) {
                    onError(e instanceof Error ? e.message : String(e));
                  }
                }}
              />
            ))}
          </tbody>
        </table>
      )}
      <AddHolidayForm
        onAdd={async (input) => {
          onError(null);
          try {
            await createWorkHoliday(input);
            await onAfterMutate();
          } catch (e) {
            onError(e instanceof Error ? e.message : String(e));
          }
        }}
      />
    </>
  );
}

function HolidayRow({
  row,
  disabled,
  onPatch,
  onRemove,
}: {
  row: WorkHolidayDto;
  disabled: boolean;
  onPatch: (patch: { date?: string; label?: string; recurring?: boolean }) => Promise<void>;
  onRemove: () => Promise<void>;
}) {
  const [date, setDate] = useState(row.date);
  const [label, setLabel] = useState(row.label);
  useEffect(() => { setDate(row.date); }, [row.date]);
  useEffect(() => { setLabel(row.label); }, [row.label]);
  return (
    <tr>
      <td>
        <input
          type="date"
          value={date}
          disabled={disabled}
          onChange={(e) => setDate(e.target.value)}
          onBlur={() => { if (date !== row.date && /^\d{4}-\d{2}-\d{2}$/.test(date)) void onPatch({ date }); }}
          style={{ width: "100%", padding: "4px 6px", fontFamily: "var(--bsp-font-mono)" }}
        />
      </td>
      <td>
        <input
          value={label}
          disabled={disabled}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={() => { if (label !== row.label && label.trim()) void onPatch({ label: label.trim() }); }}
          style={{ width: "100%", padding: "4px 6px" }}
        />
      </td>
      <td style={{ textAlign: "center" }}>
        <input
          type="checkbox"
          checked={row.recurring}
          disabled={disabled}
          onChange={(e) => void onPatch({ recurring: e.target.checked })}
        />
      </td>
      <td style={{ textAlign: "right" }}>
        <button
          type="button"
          className="btn-sm danger"
          disabled={disabled}
          onClick={() => void onRemove()}
          aria-label="Remove holiday"
        >×</button>
      </td>
    </tr>
  );
}

function AddHolidayForm({ onAdd }: { onAdd: (input: { date: string; label: string; recurring: boolean }) => Promise<void> }) {
  const [date, setDate] = useState("");
  const [label, setLabel] = useState("");
  const [recurring, setRecurring] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !label.trim()) return;
    setBusy(true);
    try {
      await onAdd({ date, label: label.trim(), recurring });
      setDate("");
      setLabel("");
      setRecurring(false);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={{
      marginTop: 10,
      padding: 10,
      border: "1px dashed var(--bsp-border-strong)",
      borderRadius: 4,
      background: "var(--bsp-surface-muted)",
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
      alignItems: "flex-end",
    }}>
      <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 10, fontWeight: 700, color: "var(--bsp-fg-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
        Date
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          disabled={busy}
          style={{ padding: "4px 6px", fontFamily: "var(--bsp-font-mono)" }}
        />
      </label>
      <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 10, fontWeight: 700, color: "var(--bsp-fg-muted)", textTransform: "uppercase", letterSpacing: "0.06em", flex: "1 1 200px" }}>
        Label
        <input
          placeholder="e.g. Christmas Day"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          disabled={busy}
          style={{ padding: "4px 6px" }}
        />
      </label>
      <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--bsp-fg-muted)" }}>
        <input
          type="checkbox"
          checked={recurring}
          disabled={busy}
          onChange={(e) => setRecurring(e.target.checked)}
        />
        Repeats yearly
      </label>
      <button
        type="button"
        className="btn-submit"
        onClick={() => void submit()}
        disabled={busy || !date || !label.trim()}
      >
        {busy ? "Adding…" : "Add holiday"}
      </button>
    </div>
  );
}
